import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/apiClient";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!ref) {
      setError("Missing order reference");
      return;
    }
    let cancelled = false;

    async function fetchOnce() {
      try {
        const { data } = await api.get(`/checkout/order/${ref}`);
        if (cancelled) return;
        setOrder(data);
        if (data.status !== "paid" && data.status !== "refused" && tries < 6) {
          // Poll for a few seconds after redirect — webhook may arrive shortly after
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e, "Could not load order"));
      }
    }
    fetchOnce();
    return () => {
      cancelled = true;
    };
  }, [ref, tries]);

  return (
    <div className="min-h-[70vh] bg-zinc-50" data-testid="checkout-return-page">
      <div className="container-oj py-16 max-w-2xl">
        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-red-800">
            {error}
          </div>
        )}
        {!order && !error && (
          <div className="rounded-2xl bg-white border border-zinc-200 p-10 text-center">
            <Loader2 className="mx-auto size-8 animate-spin text-zinc-400" />
            <p className="mt-4 text-sm text-zinc-500">Checking payment…</p>
          </div>
        )}
        {order && (
          <div className="rounded-2xl bg-white border border-zinc-200 p-10 text-center">
            {order.status === "paid" ? (
              <>
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <CheckCircle2 className="size-8" />
                </div>
                <h1 className="mt-5 font-display font-black text-3xl text-oj-ink">Payment confirmed</h1>
                <p className="mt-2 text-zinc-600">
                  Thanks — your payment for <strong>{order.offer_title}</strong> was received.
                </p>
                <p className="mt-4 text-sm text-zinc-500">
                  Amount: {order.amount.currency} {(order.amount.value / 100).toFixed(2)}
                </p>
              </>
            ) : order.status === "refused" ? (
              <>
                <div className="mx-auto w-14 h-14 rounded-full bg-red-500 text-white flex items-center justify-center">
                  <XCircle className="size-8" />
                </div>
                <h1 className="mt-5 font-display font-black text-3xl text-oj-ink">Payment refused</h1>
                <p className="mt-2 text-zinc-600">Your payment could not be processed. Please try again with a different method.</p>
              </>
            ) : (
              <>
                <div className="mx-auto w-14 h-14 rounded-full bg-amber-500 text-white flex items-center justify-center">
                  <Clock className="size-8" />
                </div>
                <h1 className="mt-5 font-display font-black text-3xl text-oj-ink">Confirming payment</h1>
                <p className="mt-2 text-zinc-600">
                  Your payment is being verified. This usually takes a few seconds.
                </p>
              </>
            )}
            <Link
              to={`/offers/${order.offer_slug}`}
              className="mt-8 inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800"
            >
              Back to offer <ArrowRight className="size-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
